import { getRangeMs } from '@chronosphereio/core';
import { ReactNode, useMemo } from 'react';
import {
  calculateAutoIntervalSeconds,
  convertMsToSeconds,
  INTERVAL_VARIABLE_AUTO_VALUE,
  intervalSizeToResolution,
  valueToVariableState,
} from '@/core';
import { useTemplateVariableDefinitions } from '@/dashboards/context';
import {
  TemplateVariableContextProvider,
  useSuggestedStepMs,
  useTemplateVariableValues,
  useTimeRange,
} from '@/plugin-system';

export const BuiltInVariablesProvider = (props: { children: ReactNode; suggestedStepMs?: number }) => {
  const defaultStepMs = useSuggestedStepMs();
  const suggestedStepMs = props.suggestedStepMs ?? defaultStepMs;
  const { absoluteTimeRange } = useTimeRange();
  const rangeMs = getRangeMs(absoluteTimeRange);
  const rangeSeconds = convertMsToSeconds(rangeMs);
  const suggestedStepSeconds = convertMsToSeconds(props.suggestedStepMs ?? defaultStepMs);
  const intervalVariableAutoOverrides = useIntervalVariableAutoOverrides();
  const panelLevelVariables = useMemo(
    () => ({
      state: {
        ...intervalVariableAutoOverrides,
        __range: valueToVariableState(`${rangeSeconds}s`),
        __range_ms: valueToVariableState(rangeMs),
        __interval: valueToVariableState(`${suggestedStepSeconds}s`),
        __interval_ms: valueToVariableState(suggestedStepMs),
      },
    }),
    [rangeMs, rangeSeconds, suggestedStepSeconds, suggestedStepMs, intervalVariableAutoOverrides]
  );

  return (
    <TemplateVariableContextProvider value={panelLevelVariables}>{props.children}</TemplateVariableContextProvider>
  );
};

/**
 * Automatically override interval variables with auto value to suggested step size.
 * Each variable can specify its own auto_interval_size level (Small, Medium, Large) which
 * affects the number of data points and thus the calculated step size.
 */
function useIntervalVariableAutoOverrides() {
  const definitions = useTemplateVariableDefinitions();
  const { absoluteTimeRange } = useTimeRange();

  const { intervalVariableNames, intervalVariableDefinitions } = useMemo(() => {
    const intervalVariableDefinitions = definitions.filter((def) => def.kind === 'IntervalVariable');
    const intervalVariableNames = intervalVariableDefinitions.map((def) => def.spec.name);
    return { intervalVariableDefinitions, intervalVariableNames };
  }, [definitions]);

  const values = useTemplateVariableValues(intervalVariableNames);
  return useMemo(() => {
    for (const name of intervalVariableNames) {
      const definition = intervalVariableDefinitions.find((def) => def.spec.name === name);
      const value = values[name]?.value;
      if (value === INTERVAL_VARIABLE_AUTO_VALUE && definition?.kind === 'IntervalVariable') {
        // Map interval size to internal resolution level for calculations
        const intervalSize = definition.spec.auto_interval_size;
        const resolution = intervalSize ? intervalSizeToResolution[intervalSize] : undefined;
        const intervalValue = calculateAutoIntervalSeconds({
          timeRange: absoluteTimeRange,
          resolution,
          minInterval: definition.spec.auto_min_interval,
        });

        // eslint-disable-next-line react-hooks/immutability
        values[name] = {
          value: intervalValue,
          loading: false,
        };
      }
    }
    return values;
  }, [intervalVariableNames, absoluteTimeRange, values, intervalVariableDefinitions]);
}
